import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import EcxSurveyEditor from '../../helpers/ecxsurvey/EcxSurveyEditor';
import { useSurveyStore } from '../../helpers/ecxsurvey/surveyutilities/surveyStore';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import surveyService from '../../../services/survey.service';
import SubHeader from '../../layout/components/SubHeader';

function CreateSurveyPage() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const navigate = useNavigate();
    const exportToJSON = useSurveyStore((state) => state.exportToJSON);
    const resetStore = useSurveyStore((state) => state.resetStore);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        resetStore();
    }, [resetStore]);

    const buildPayload = (statusOverride) => {
        const surveyData = exportToJSON();
        const { id, ...surveyWithoutId } = surveyData.survey || {};
        return {
            survey: {
                ...surveyWithoutId,
                status: statusOverride || surveyWithoutId.status || 'DRAFT',
            },
            questions: surveyData.questions || [],
        };
    };

    const ensureHasQuestions = () => {
        const { questions } = exportToJSON();
        if (!Array.isArray(questions) || questions.length === 0) {
            toast.error('Add at least one question before saving the survey');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!clinicId || saving) return;
        if (!ensureHasQuestions()) return;
        setSaving(true);
        try {
            const response = await surveyService.createClinicSurvey(clinicId, buildPayload('DRAFT'));
            const surveyId = response.data?.survey?.id;
            toast.success('Survey saved as draft');
            if (surveyId) {
                navigate(`/surveys/${surveyId}/edit`, { replace: true });
            } else {
                navigate('/surveys');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to save survey');
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        if (!clinicId || saving) return;
        if (!ensureHasQuestions()) return;
        setSaving(true);
        try {
            const response = await surveyService.createClinicSurvey(clinicId, buildPayload('ACTIVE'));
            toast.success('Survey created and activated');
            const surveyId = response.data?.survey?.id;
            navigate(surveyId ? `/surveys/${surveyId}/edit` : '/surveys', { replace: true });
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to publish survey');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ecx-page surveys-page ecx-page--full-width">
            <SubHeader
                title="Create survey"
                description="Build a new clinic survey from scratch"
                icon="bi-clipboard-check"
                containerClass="w-100 px-4"
            >
                <Link to="/surveys" className="btn btn-sm btn-outline-secondary rounded-pill">
                    <i className="bi bi-chevron-left me-1" />
                    Back to surveys
                </Link>
            </SubHeader>
            <div className="ecx-page-body">
                <EcxSurveyEditor onSave={handleSave} onPublish={handlePublish} />
            </div>
        </div>
    );
}

export default CreateSurveyPage;
